import Icon from '@ant-design/icons'
import { DecryptedPatient } from '@icure/cardinal-sdk'
import { createSelector } from '@reduxjs/toolkit'
import { Tag } from 'antd'
import React, { ReactElement } from 'react'
import { emailIcn, locationIcn, phoneIcn, userAvatarPlaceholderIcn } from '../../../../assets/CustomIcons'
import { useAppSelector } from '../../../../core/hooks'
import { CardinalApiState } from '../../../../core/services/auth.api'
import { getPatientDataFormated } from '../../../../helpers/patientDataManipulations'
import { CommonPlaceholder } from '../../../common/CommonPlaceholder'

import { CustomModal } from '../../../common/CustomModal'
import './index.less'
import { SpinLoader } from '../../../common/SpinLoader'
import { getTagColor } from '../../../../helpers/getTagColor'
import { Consultation } from './Consultation'
import { useFindFormsByHcPartyPatientQuery } from '../../../../core/api/formApi'
import { useGetPatientsByIdsQuery } from '../../../../core/api/patientApi'
import { useGetPractitionersQuery } from '../../../../core/api/practitionerApi'

interface ModalPatientProfileProps {
  isVisible: boolean
  patientId: string
  onClose: () => void
  onEdit: () => void
  onAddConsultation: () => void
}

const reduxSelector = createSelector(
  (state: { cardinalApi: CardinalApiState }) => state.cardinalApi,
  (cardinalApi: CardinalApiState) => ({
    healthcarePartyId: cardinalApi.user?.healthcarePartyId,
    parentHealthcarePartyId: cardinalApi.healthcareParty?.parentId,
  }),
)

export const ModalPatientProfile = ({ isVisible, onClose, patientId, onEdit, onAddConsultation }: ModalPatientProfileProps): ReactElement => {
  const { healthcarePartyId, parentHealthcarePartyId } = useAppSelector(reduxSelector)

  const { data: patientWrapper } = useGetPatientsByIdsQuery([patientId], { skip: !patientId })
  const patient = patientWrapper?.[0] as DecryptedPatient
  const { emailAddress, phoneNumber, userNameOneString, tags, userHomeAddressOneString, birthSex, language, userDateOfBirthOneString, age, delegatesIds } =
    getPatientDataFormated(patient)

  const { data: delegatesPractitioners } = useGetPractitionersQuery(delegatesIds ?? [], { skip: delegatesIds?.length === 0 })
  const {
    data: listOfForms,
    isLoading: isListOfFormsLoading,
    error,
  } = useFindFormsByHcPartyPatientQuery(
    {
      hcPartyId: parentHealthcarePartyId ?? healthcarePartyId ?? '',
      patient,
    },
    { skip: (!healthcarePartyId && !parentHealthcarePartyId) || !patient },
  )
  const sortedForms = [...(listOfForms ?? [])]?.sort((a, b) => (b.openingDate ?? 0) - (a.openingDate ?? 0))

  return (
    <CustomModal
      isVisible={isVisible}
      handleClose={onClose}
      title="Patient profile"
      secondaryBtnTitle="Edit patient"
      handleClickSecondaryBtn={onEdit}
      primaryBtnTitle="Start consultation"
      handleClickPrimaryBtn={onAddConsultation}
      width={1200}
    >
      <div className="modalPatienProfile">
        {(isListOfFormsLoading || (listOfForms && listOfForms?.length !== 0 && sortedForms?.length === 0)) && <SpinLoader />}
        <div className="modalPatienProfile__innerContainer">
          <div className="modalPatienProfile__shortInfo">
            <div className="modalPatienProfile__shortInfo__leftBlock">
              <div className="modalPatienProfile__shortInfo__intro">
                <div className="modalPatienProfile__shortInfo__intro__userAvatarPlaceholder">
                  <Icon component={userAvatarPlaceholderIcn} />
                </div>

                <h3 className="modalPatienProfile__shortInfo__name">{userNameOneString}</h3>
              </div>
              <div className="modalPatienProfile__shortInfo__contactDetails">
                <h4>Contact Details:</h4>
                <div className="modalPatienProfile__shortInfo__contactDetails__itemsContainer">
                  <div className="modalPatienProfile__shortInfo__contactDetails__item">
                    <Icon component={phoneIcn} />
                    <p>{phoneNumber ?? '-'}</p>
                  </div>
                  <div className="modalPatienProfile__shortInfo__contactDetails__item">
                    <Icon component={emailIcn} />
                    <p>{emailAddress ?? '-'}</p>
                  </div>
                  <div className="modalPatienProfile__shortInfo__contactDetails__item">
                    <Icon component={locationIcn} />
                    <p>{userHomeAddressOneString ?? '-'}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="modalPatienProfile__shortInfo__rightBlock">
              <div className="modalPatienProfile__shortInfo__overview">
                <h4>Overview:</h4>
                <div className="modalPatienProfile__shortInfo__overview__itemsWrap">
                  <div className="modalPatienProfile__shortInfo__overview__item">
                    <h5>Date of birth:</h5>
                    <p>{userDateOfBirthOneString ?? '-'}</p>
                  </div>
                  <div className="modalPatienProfile__shortInfo__overview__item">
                    <h5>Age:</h5>
                    <p>{age ?? '-'}</p>
                  </div>
                  <div className="modalPatienProfile__shortInfo__overview__item">
                    <h5>Sex at birth:</h5>
                    <p>{birthSex ?? '-'}</p>
                  </div>
                  <div className="modalPatienProfile__shortInfo__overview__item">
                    <h5>Language:</h5>
                    <p>{language ?? '-'}</p>
                  </div>
                </div>
              </div>
              {tags?.length !== 0 && (
                <div className="modalPatienProfile__shortInfo__overview">
                  <h4>Tags:</h4>
                  <div className="modalPatienProfile__shortInfo__overview__tags">
                    {tags?.map((tag, index) => (
                      <Tag key={index} color={getTagColor(tag.code)} bordered={false}>
                        {tag.code}
                      </Tag>
                    ))}
                  </div>
                </div>
              )}
              {!!delegatesPractitioners && delegatesPractitioners?.length !== 0 && (
                <div className="modalPatienProfile__shortInfo__overview">
                  <h4>Shared with:</h4>
                  <div className="modalPatienProfile__shortInfo__overview__item">
                    <p>
                      {delegatesPractitioners?.map((practitioner, index) => (
                        <span key={index}>
                          {practitioner.firstName} {practitioner.lastName}
                          {index !== delegatesPractitioners.length - 1 ? ', ' : ''}
                        </span>
                      ))}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
          <h4 className="consultationsHeading">Consultations:</h4>
          {/*Consultations*/}
          {sortedForms?.length !== 0 && sortedForms?.map((form, index) => <Consultation form={form} patient={patient} key={index} />)}

          {listOfForms?.length === 0 && (
            <CommonPlaceholder title="No Consultations Available" content="This patient has no consultations recorded. Add a new consultation to get started!" />
          )}
          {!listOfForms && !!error && (
            <CommonPlaceholder
              danger
              title="Consultations Not Available"
              content="
              Consultations are securely encrypted to protect patient privacy.
              To view them, we need your recovery key.

              This might happen if you're using a new device or browser.
              Just head to 'My Account' → 'Provide Recovery Key' and enter your recovery key to unlock the data.
              "
            />
          )}
        </div>
      </div>
    </CustomModal>
  )
}
